import { useMemo, useRef, useState, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { CCard } from "@coreui/react-pro";
import type { TrackerStrategyResultDto } from "../../../api/hooks/usePortfolioTracker";
import {
  chartFrame,
  formatDay,
  formatSignedMoney,
  lineBounds,
  profitSeries,
  valueAt,
} from "../portfolio-tracker";
import { useElementSize } from "../useElementSize";
import { ChartTip, SteppedLine } from "./chart-parts";

interface ProfitOverTimeProps {
  name: string;
  result: TrackerStrategyResultDto;
  asOf: string;
}

/** One strategy's profit so far, from nothing at its start, moving as each of its trades closes. */
export function ProfitOverTime({ name, result, asOf }: ProfitOverTimeProps) {
  const { t } = useTranslation("portfolioTracker");
  const hostRef = useRef<HTMLDivElement>(null);
  const size = useElementSize(hostRef);
  const [hoverDay, setHoverDay] = useState<string | null>(null);

  const points = useMemo(() => profitSeries(result, asOf), [result, asOf]);
  const width = Math.max(260, size.width);
  const height = Math.max(120, size.height);
  const frame = useMemo(() => {
    const [low, high] = lineBounds(points, 0);
    return chartFrame(width, height, low, high, points[0].date, asOf);
  }, [points, width, height, asOf]);

  const handleMove = (event: PointerEvent<HTMLDivElement>) =>
    setHoverDay(frame.dayAt(event.clientX - event.currentTarget.getBoundingClientRect().left));
  const handleLeave = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse") setHoverDay(null);
  };

  const hoverValue = hoverDay ? valueAt(points, hoverDay) : 0;
  const last = points[points.length - 1];

  return (
    <CCard className="tracker-chart-card">
      <header className="tracker-chart-head">
        <h3 className="h6 mb-0">{t("strategy.chartTitle")}</h3>
        <div className="tracker-legend">
          <span>
            <i className="tracker-key-line" />
            {t("strategy.keyProfit", { name })}
          </span>
          <span>
            <i className="tracker-key-base" />
            {t("strategy.keyZero")}
          </span>
        </div>
      </header>
      <div className="tracker-chart-wrap" onPointerMove={handleMove} onPointerDown={handleMove} onPointerLeave={handleLeave}>
        <div ref={hostRef} className="tracker-chart">
          {size.width > 0 && (
            <svg
              viewBox={`0 0 ${width} ${height}`}
              role="img"
              aria-label={t("strategy.chartLabel", { from: formatDay(points[0].date), profit: formatSignedMoney(last.value) })}
            >
              <SteppedLine
                frame={frame}
                points={points}
                base={0}
                endLabel={formatSignedMoney(last.value)}
                zone={null}
                hoverDay={hoverDay}
                hoverValue={hoverValue}
              />
            </svg>
          )}
          {hoverDay && size.width > 0 && (
            <ChartTip frame={frame} x={frame.x(hoverDay)}>
              <b>{t("strategy.tip", { profit: formatSignedMoney(hoverValue) })}</b>
              <span>{formatDay(hoverDay)}</span>
            </ChartTip>
          )}
        </div>
      </div>
      <p className="tracker-chart-note mb-0">{t("strategy.chartNote")}</p>
    </CCard>
  );
}
